import { httpClient, toClientApiError } from "./httpClient";

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

/* ── types (server: application/mailing, ADR-0033) ── */

export type MailingBlockType = "heading" | "text" | "button" | "image" | "divider" | "spacer" | "html";
export type MailingBlock = {
  id: string;
  type: MailingBlockType;
  text?: string;
  size?: "l" | "m" | "s";
  align?: "right" | "center" | "left";
  label?: string;
  url?: string;
  color?: string;
  src?: string;
  alt?: string;
  href?: string;
  width?: number;
  height?: number;
  html?: string;
};
export type MailingDesign = {
  blocks: MailingBlock[];
  settings?: { background?: string; contentBackground?: string; textColor?: string; accent?: string; width?: number };
};

export type MailingAudience = {
  source?: "customers" | "subscribers" | "manual";
  q?: string;
  filters?: Record<string, unknown>;
  customerIds?: string[];
  emailFields?: Array<"customer" | "contact">;
  entries?: Array<{ email: string; name?: string }>;
  consentMode?: "all" | "consented";
};

export type AudienceStats = {
  contacts: number;
  withEmail: number;
  invalid: number;
  duplicates: number;
  suppressed: number;
  notConsented: number;
  final: number;
};

export type BlastCounts = {
  recipients: number;
  suppressed: number;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  failed: number;
  opened: number;
  clicked: number;
  unsubscribed: number;
};

export type BlastStatus = "draft" | "scheduled" | "sending" | "paused" | "sent" | "cancelled" | "active";

export type BlastDto = {
  id: string;
  kind: "blast" | "welcome";
  name: string;
  subject: string | null;
  preheader: string | null;
  fromName: string | null;
  replyTo: string | null;
  audience: MailingAudience;
  isAdvertising: boolean;
  status: BlastStatus;
  scheduledAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  counts: BlastCounts;
  ab: { enabled: boolean; subjectB: string; testPercent: number; waitMinutes: number; metric: "open" | "click"; winner: string | null; decidedAt: string | null };
  lastError: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  design?: MailingDesign | null;
  html?: string | null;
};

export type BlastInput = Partial<{
  name: string;
  subject: string;
  preheader: string;
  fromName: string;
  replyTo: string;
  isAdvertising: boolean;
  audience: MailingAudience;
  design: MailingDesign;
  ab: { enabled: boolean; subjectB: string; testPercent: number; waitMinutes: number; metric: "open" | "click" };
  templateId: string;
}>;

export type QuotaDto = {
  period: string;
  quota: number;
  used: number;
  pending: number;
  remaining: number;
  allowOverage: boolean;
  suspended: boolean;
  suspendedReason: string | null;
};

export type MailingSettingsDto = {
  orgName: string;
  senderName: string;
  replyTo: string;
  footerAddress: string;
  monthlyQuota: number;
  customQuota: boolean;
  allowOverage: boolean;
  suspended: boolean;
  suspendedReason: string | null;
  quota: QuotaDto;
};

export type MailingOverviewDto = {
  quota: QuotaDto;
  blasts: Record<string, number>;
  last30: { sent: number; opened: number; clicked: number; bounced: number; unsubscribed: number };
  recent: BlastDto[];
  subscribers: number;
  suppressions: number;
};

export type TemplateDto = { id: string; name: string; subject: string | null; preheader: string | null; html: string | null; updatedAt: string; design?: MailingDesign | null };

export type BlastReportDto = {
  blast: BlastDto;
  byStatus: Record<string, number>;
  links: Array<{ link: string; clicks: number; people: number }>;
  timeline: Array<{ hour: string; type: string; count: number }>;
  ab: { A: { sent: number; opened: number; clicked: number }; B: { sent: number; opened: number; clicked: number } } | null;
};

export type RecipientRowDto = {
  id: string;
  email: string;
  name: string | null;
  customerId: string | null;
  status: string;
  variant: string | null;
  error: string | null;
  sentAt: string | null;
  openedAt: string | null;
  openCount: number;
  clickedAt: string | null;
  clickCount: number;
  bouncedAt: string | null;
  unsubscribedAt: string | null;
};

export type SubscriberDto = {
  id: string;
  email: string;
  name: string | null;
  source: string;
  note: string | null;
  status: "pending" | "confirmed";
  consentedAt: string;
  confirmedAt: string | null;
  formTitle: string | null;
};

export type SuppressionDto = { id: string; email: string; reason: string; reasonLabel: string; note: string | null; createdAt: string };

export type SignupFormDto = {
  id: string;
  token: string;
  url: string;
  embed: string;
  title: string;
  description: string | null;
  buttonLabel: string | null;
  successMessage: string | null;
  collectName: boolean;
  doubleOptIn: boolean;
  isActive: boolean;
  welcome: { blastId: string; enabled: boolean; subject: string | null; sent: number } | null;
  subscribers: number;
  pending: number;
  createdAt: string;
};

export type SignupFormInput = Partial<Omit<SignupFormDto, "id" | "token" | "url" | "embed" | "welcome" | "subscribers" | "pending" | "createdAt">> & {
  welcomeEnabled?: boolean;
};

export type CustomerMailingDto = {
  id: string;
  blastId: string;
  blastName: string;
  subject: string | null;
  email: string;
  status: string;
  sentAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  unsubscribedAt: string | null;
};

type Page<T> = { items: T[]; total: number };
const qs = (params: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""));

export const mailingApi = {
  overview: () => wrap(async () => (await httpClient.get<{ item: MailingOverviewDto }>("/mailing/overview")).data.item),
  settings: () => wrap(async () => (await httpClient.get<{ item: MailingSettingsDto }>("/mailing/settings")).data.item),
  saveSettings: (patch: Partial<Pick<MailingSettingsDto, "senderName" | "replyTo" | "footerAddress">>) =>
    wrap(async () => (await httpClient.put<{ item: MailingSettingsDto }>("/mailing/settings", patch)).data.item),
  mergeTags: () => wrap(async () => (await httpClient.get<{ items: Array<{ key: string; label: string }> }>("/mailing/merge-tags")).data.items),
  render: (design: MailingDesign) => wrap(async () => (await httpClient.post<{ item: { html: string } }>("/mailing/render", { design })).data.item.html),

  templates: {
    list: () => wrap(async () => (await httpClient.get<{ items: TemplateDto[] }>("/mailing/templates")).data.items),
    get: (id: string) => wrap(async () => (await httpClient.get<{ item: TemplateDto }>(`/mailing/templates/${id}`)).data.item),
    save: (id: string | null, input: { name: string; subject?: string | null; preheader?: string | null; design: MailingDesign }) =>
      wrap(async () =>
        id
          ? (await httpClient.put<{ item: TemplateDto }>(`/mailing/templates/${id}`, input)).data.item
          : (await httpClient.post<{ item: TemplateDto }>("/mailing/templates", input)).data.item,
      ),
    remove: (id: string) => wrap(async () => void (await httpClient.delete(`/mailing/templates/${id}`))),
  },

  blasts: {
    list: (params: { status?: string; q?: string; page?: number; pageSize?: number } = {}) =>
      wrap(async () => (await httpClient.get<Page<BlastDto>>("/mailing/blasts", { params: qs(params) })).data),
    get: (id: string) => wrap(async () => (await httpClient.get<{ item: BlastDto }>(`/mailing/blasts/${id}`)).data.item),
    create: (input: BlastInput) => wrap(async () => (await httpClient.post<{ item: BlastDto }>("/mailing/blasts", input)).data.item),
    update: (id: string, input: BlastInput) => wrap(async () => (await httpClient.put<{ item: BlastDto }>(`/mailing/blasts/${id}`, input)).data.item),
    duplicate: (id: string) => wrap(async () => (await httpClient.post<{ item: BlastDto }>(`/mailing/blasts/${id}/duplicate`)).data.item),
    remove: (id: string) => wrap(async () => void (await httpClient.delete(`/mailing/blasts/${id}`))),
    test: (id: string, emails: string[]) =>
      wrap(async () => (await httpClient.post<{ item: { results: Array<{ email: string; ok: boolean; error?: string }> } }>(`/mailing/blasts/${id}/test`, { emails })).data.item),
    launch: (id: string, scheduledAt: string | null) =>
      wrap(async () => (await httpClient.post<{ item: { blast: BlastDto; stats: AudienceStats } }>(`/mailing/blasts/${id}/launch`, { scheduledAt })).data.item),
    pause: (id: string) => wrap(async () => (await httpClient.post<{ item: BlastDto }>(`/mailing/blasts/${id}/pause`)).data.item),
    resume: (id: string) => wrap(async () => (await httpClient.post<{ item: BlastDto }>(`/mailing/blasts/${id}/resume`)).data.item),
    cancel: (id: string) => wrap(async () => (await httpClient.post<{ item: BlastDto }>(`/mailing/blasts/${id}/cancel`)).data.item),
    report: (id: string) => wrap(async () => (await httpClient.get<{ item: BlastReportDto }>(`/mailing/blasts/${id}/report`)).data.item),
    recipients: (id: string, params: { status?: string; q?: string; page?: number; pageSize?: number } = {}) =>
      wrap(async () => (await httpClient.get<Page<RecipientRowDto>>(`/mailing/blasts/${id}/recipients`, { params: qs(params) })).data),
  },

  previewAudience: (audience: MailingAudience) =>
    wrap(async () =>
      (await httpClient.post<{ item: { stats: AudienceStats; sample: Array<{ email: string; name: string }> } }>("/mailing/audience-preview", { audience })).data.item,
    ),
  parseList: (text: string) =>
    wrap(async () => (await httpClient.post<{ item: { entries: Array<{ email: string; name: string }>; valid: number } }>("/mailing/parse-list", { text })).data.item),

  subscribers: {
    list: (params: { q?: string; status?: string; page?: number; pageSize?: number } = {}) =>
      wrap(async () => (await httpClient.get<Page<SubscriberDto>>("/mailing/subscribers", { params: qs(params) })).data),
    add: (input: { text: string; source?: "manual" | "import"; note?: string }) =>
      wrap(async () => (await httpClient.post<{ item: { added: number; invalid: number } }>("/mailing/subscribers", input)).data.item),
    remove: (id: string) => wrap(async () => void (await httpClient.delete(`/mailing/subscribers/${id}`))),
  },

  suppressions: {
    list: (params: { q?: string; reason?: string; page?: number; pageSize?: number } = {}) =>
      wrap(async () => (await httpClient.get<Page<SuppressionDto> & { counts: Record<string, number> }>("/mailing/suppressions", { params: qs(params) })).data),
    add: (input: { text: string; note?: string }) =>
      wrap(async () => (await httpClient.post<{ item: { added: number } }>("/mailing/suppressions", input)).data.item),
    remove: (id: string, force = false) => wrap(async () => void (await httpClient.delete(`/mailing/suppressions/${id}`, { params: force ? { force: 1 } : {} }))),
  },

  forms: {
    list: () => wrap(async () => (await httpClient.get<{ items: SignupFormDto[] }>("/mailing/forms")).data.items),
    save: (id: string | null, input: SignupFormInput) =>
      wrap(async () =>
        id
          ? (await httpClient.put<{ item: SignupFormDto }>(`/mailing/forms/${id}`, input)).data.item
          : (await httpClient.post<{ item: SignupFormDto }>("/mailing/forms", input)).data.item,
      ),
    remove: (id: string) => wrap(async () => void (await httpClient.delete(`/mailing/forms/${id}`))),
  },

  customerHistory: (customerId: string) =>
    wrap(async () => (await httpClient.get<{ items: CustomerMailingDto[] }>(`/customers/${customerId}/mailing`)).data.items),

  platform: {
    get: (tenantId: string) => wrap(async () => (await httpClient.get<{ item: MailingSettingsDto }>(`/platform/tenants/${tenantId}/mailing`)).data.item),
    save: (tenantId: string, patch: { monthlyQuota?: number | null; allowOverage?: boolean; suspended?: boolean; suspendedReason?: string | null }) =>
      wrap(async () => (await httpClient.put<{ item: MailingSettingsDto }>(`/platform/tenants/${tenantId}/mailing`, patch)).data.item),
  },
};
